import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Heading,
	HStack,
	Input,
	InputGroup,
	InputLeftAddon,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Select,
	Stack,
	Textarea,
	useToast,
} from "@chakra-ui/react";
import { IoChevronBackOutline } from "react-icons/io5";
import CertificateIcon from "../../assets/certificate-icon.png";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import React from "react";
import { FaUpload } from "react-icons/fa";
import {
	ADD_PROJECT_HISTORY,
	ADD_EMPLOYMENT_HISTORY_ITEM,
} from "src/common/graphql/typed-mutations";
import {
	manualEmploymentSchema,
	manualProjectSchema,
} from "src/features/pro-portfolio/portfolio-input-validation";

const projectFormSchema = manualProjectSchema
	.extend({
		budget: z.number().optional(),
		proDoesNotWishToDiscloseBudget: z.boolean().optional(),
		images: z
			.array(
				z.object({
					name: z.string(),
					data: z.string(),
					mimeType: z.string(),
				})
			)
			.optional(),
	})
	.refine(
		(data) => {
			if (!data.budget && !data.proDoesNotWishToDiscloseBudget) {
				return false;
			}

			return true;
		},
		{
			message: "A budget is required unless you do not wish to disclose it",
			path: ["budget"],
		}
	);

const employmentFormSchema = manualEmploymentSchema
	.extend({
		currentlyWorking: z.boolean().optional(),
		monthlySalary: z.number().optional(),
		proDoesNotWishToDiscloseMonthlySalary: z.boolean().optional(),
		images: z
			.array(
				z.object({
					name: z.string(),
					data: z.string(),
					mimeType: z.string(),
				})
			)
			.optional(),
	})
	.refine(
		(data) => {
			if (!data.monthlySalary && !data.proDoesNotWishToDiscloseMonthlySalary) {
				return false;
			}

			return true;
		},
		{
			message:
				"Monthly salary is required unless you do not wish to disclose it",
			path: ["monthlySalary"],
		}
	);

type JobType = "project" | "employment";

export const AddJob = () => {
	const [query] = useSearchParams();

	let jobTypeQuery = query.get("job-type");

	if (
		jobTypeQuery &&
		jobTypeQuery !== "project" &&
		jobTypeQuery !== "employment"
	) {
		jobTypeQuery = "employment";
	}

	const [jobType, setJobType] = React.useState<JobType>(
		(jobTypeQuery as JobType) || "employment"
	);
	const [selectedPortfolioImages, setSelectedPortfolioImages] = React.useState<
		string[]
	>([]);

	const navigate = useNavigate();
	const toast = useToast();

	const [addProjectHistory, addProjectHistoryHandle] = useMutation(
		ADD_PROJECT_HISTORY,
		{
			onCompleted: () => {
				navigate("/my-laborpass");
			},
			onError: () => {
				toast({
					title: "Failed to add job",
					description: "Something went wrong. Please try again.",
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const [addEmploymentHistoryItem, addEmploymentHistoryItemHandle] =
		useMutation(ADD_EMPLOYMENT_HISTORY_ITEM, {
			onCompleted: () => {
				navigate("/my-laborpass");
			},
			onError: () => {
				toast({
					title: "Failed to add job",
					description: "Something went wrong. Please try again.",
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		});

	const {
		register: registerProject,
		watch: watchProject,
		setValue: setProjectValue,
		handleSubmit: handleSubmitProject,
		formState: { errors: projectErrors },
	} = useForm<z.infer<typeof projectFormSchema>>({
		resolver: zodResolver(projectFormSchema),
	});

	const {
		register: registerEmployment,
		watch: watchEmployment,
		setValue: setEmploymentValue,
		handleSubmit: handleSubmitEmployment,
		formState: { errors: employmentErrors },
	} = useForm<z.infer<typeof employmentFormSchema>>({
		resolver: zodResolver(employmentFormSchema),
	});

	const onSubmitProject = (data: z.infer<typeof projectFormSchema>) => {
		const images = data.images || [];
		addProjectHistory({
			variables: {
				name: data.taskName,
				description: data.taskDescription,
				...(data.budget && data.budget > 0 && { budget: data.budget }),
				proDoesNotWishToDiscloseBudget: !!data.proDoesNotWishToDiscloseBudget,
				startedAt: null,
				images,
				videos: [],
				endedAt: new Date(data.date),
			},
		});
	};

	const onSubmitEmployment = (data: z.infer<typeof employmentFormSchema>) => {
		const images = data.images || [];
		addEmploymentHistoryItem({
			variables: {
				companyName: data.companyName,
				jobTitle: data.jobTitle,
				description: data.jobDescription,
				startedAt: new Date(data.startDate),
				endedAt:
					data.currentlyWorking || !data.endDate
						? null
						: new Date(data.endDate),
				proDoesNotWishToDiscloseMonthlySalary:
					!!data.proDoesNotWishToDiscloseMonthlySalary,
				...(data.monthlySalary &&
					data.monthlySalary > 0 && { monthlySalary: data.monthlySalary }),
				images,
			},
		});
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		const files = Array.from(e.target.files);

		files.forEach((file) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (!reader.result) return;

				if (typeof reader.result === "string") {
					const image = reader.result;

					setSelectedPortfolioImages((prev) => [...prev, image]);

					const dataObject = {
						name:
							"job-history-image-" +
							reader.result.split(",")[1].substring(0, 5) +
							`-${Math.random() * 1000}`,
						data: reader.result.split(",")[1],
						mimeType: file.type,
					};

					if (jobType === "project") {
						const currentImages = watchProject("images") || [];
						setProjectValue("images", [...currentImages, dataObject]);
					} else {
						const currentImages = watchEmployment("images") || [];
						setEmploymentValue("images", [...currentImages, dataObject]);
					}
				}
			};

			reader.readAsDataURL(file);
		});
	};

	const budget = watchProject("budget");
	const proDoesNotWishToDiscloseBudget = watchProject(
		"proDoesNotWishToDiscloseBudget"
	);
	const currentlyWorking = watchEmployment("currentlyWorking");
	const monthlySalary = watchEmployment("monthlySalary");
	const proDoesNotWishToDiscloseMonthlySalary = watchEmployment(
		"proDoesNotWishToDiscloseMonthlySalary"
	);
	const imageSectionTitle =
		jobType === "project" ? "Project Photos" : "Work Photos";
	const imageSectionDescription =
		jobType === "project"
			? "Photos are optional, but they are what populate your LaborPass gallery and help clients see the project you completed."
			: "Photos are optional, but they are what populate your LaborPass gallery and help clients see the work you handled in this role.";

	return (
		<>
			<Stack spacing={0}>
				<form
					onSubmit={
						jobType === "project"
							? handleSubmitProject(onSubmitProject)
							: handleSubmitEmployment(onSubmitEmployment)
					}
					className="flex flex-col space-y-8 lg:space-y-12"
				>
					<Stack
						spacing={0}
						px={{
							base: 8,
							lg: 20,
						}}
					>
						<HStack
							borderBottom={"1px"}
							borderColor={"#E7E7E7"}
							py={6}
							textColor={"#212121"}
							mb={{
								base: 4,
								lg: 8,
							}}
						>
							<Link to="/my-laborpass">
								<div className="hover:cursor-pointer flex items-center gap-3">
									<IoChevronBackOutline />
									<span className="text-base lg:text-lg">Back</span>
								</div>
							</Link>
						</HStack>

						<div className="overflow-y-scroll grid grid-cols-1 lg:grid-cols-2 lg:border-[1px] border-[#C9C9C9] lg:px-12">
							<div className="hidden lg:block py-8">
								<div className="mb-8 bg-[#EDEDED] w-[72px] h-[72px] rounded-full flex items-center justify-center p-6">
									<img src={CertificateIcon} alt="Certificate Icon" />
								</div>
								<div className="flex flex-col w-[70%]">
									<h2 className="mb-2 text-xl lg:text-2xl font-medium text-[#121212] font-[AeonikPro]">
										Upload a job history
									</h2>
									<p className="text-lg lg:text-xl text-[#464646]">
										Provide your job details in the required fields.
									</p>
								</div>
							</div>
							<div className="border-l-[0] lg:border-l-[1px] border-[#E7E7E7] lg:pl-12 py-8 flex flex-col gap-6">
								<div className="flex flex-col gap-6 lg:gap-8">
									<FormControl>
										<FormLabel htmlFor="jobType">Job Type</FormLabel>
										<Select
											id="jobType"
											value={jobType}
											onChange={(e) => setJobType(e.target.value as JobType)}
											variant="outline"
											rounded={0}
											border={"1px solid #D7D7D7"}
										>
											<option value="project">Gig/Project</option>
											<option value="employment">
												Long-term (Full time/Contract)
											</option>
										</Select>
									</FormControl>

									{jobType === "project" ? (
										<>
											<FormControl isInvalid={!!projectErrors.taskName}>
												<FormLabel htmlFor="taskName">Project Name</FormLabel>
												<Input
													id="taskName"
													placeholder="Example: 2HP Split AC Installation"
													{...registerProject("taskName")}
													variant="outline"
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{projectErrors.taskName?.message}
												</FormErrorMessage>
											</FormControl>
											<FormControl isInvalid={!!projectErrors.taskDescription}>
												<FormLabel htmlFor="taskDescription">
													Project Description
												</FormLabel>
												<Textarea
													id="taskDescription"
													placeholder="Example: I installed a 2HP split AC in the second floor living room."
													{...registerProject("taskDescription")}
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{projectErrors.taskDescription?.message}
												</FormErrorMessage>
											</FormControl>
											<FormControl isInvalid={!!projectErrors.date}>
												<FormLabel htmlFor="taskDate">
													Completion Date
												</FormLabel>
												<Input
													id="taskDate"
													{...registerProject("date")}
													type="date"
													variant="outline"
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{projectErrors.date?.message}
												</FormErrorMessage>
											</FormControl>
											<FormControl isInvalid={!!projectErrors.budget}>
												<FormLabel htmlFor="budget">Project Budget</FormLabel>
												<div className="flex flex-col gap-2">
													<FormControl
														isInvalid={
															!!projectErrors.proDoesNotWishToDiscloseBudget
														}
													>
														<div className="flex items-center gap-2">
															<Checkbox
																id="proDoesNotWishToDiscloseBudget"
																colorScheme="brand"
																{...registerProject(
																	"proDoesNotWishToDiscloseBudget"
																)}
																isDisabled={!!(budget && budget > 0)}
															/>
															<FormLabel
																fontSize={"14px"}
																color={"gray.500"}
																margin={0}
																fontWeight={"normal"}
																htmlFor="proDoesNotWishToDiscloseBudget"
															>
																I do not wish to disclose the budget
															</FormLabel>
														</div>
													</FormControl>
													<InputGroup>
														<InputLeftAddon
															rounded={0}
															border={"1px solid #D7D7D7"}
														>
															₦
														</InputLeftAddon>
														<NumberInput
															className="w-full"
															onChange={(_, value) => {
																if (isNaN(value)) {
																	setProjectValue("budget", 0);
																	return;
																}
																setProjectValue("budget", value);
															}}
															isDisabled={!!proDoesNotWishToDiscloseBudget}
															value={watchProject("budget")}
															step={10000}
															rounded={0}
															border={"#D7D7D7"}
														>
															<NumberInputField rounded={0} />
															<NumberInputStepper>
																<NumberIncrementStepper />
																<NumberDecrementStepper />
															</NumberInputStepper>
														</NumberInput>
													</InputGroup>
												</div>
												<FormHelperText fontSize={"14px"} color={"gray.500"}>
													This is not displayed publicly
												</FormHelperText>
												<FormErrorMessage>
													{projectErrors.budget?.message}
												</FormErrorMessage>
											</FormControl>
										</>
									) : (
										<>
											<FormControl isInvalid={!!employmentErrors.companyName}>
												<FormLabel htmlFor="companyName">
													Company Name
												</FormLabel>
												<Input
													id="companyName"
													placeholder="Example: ABC Corporation"
													{...registerEmployment("companyName")}
													variant="outline"
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{employmentErrors.companyName?.message}
												</FormErrorMessage>
											</FormControl>

											<FormControl isInvalid={!!employmentErrors.jobTitle}>
												<FormLabel htmlFor="jobTitle">Job Title</FormLabel>
												<Input
													id="jobTitle"
													placeholder="Example: Senior Electrician"
													{...registerEmployment("jobTitle")}
													variant="outline"
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{employmentErrors.jobTitle?.message}
												</FormErrorMessage>
											</FormControl>

											<FormControl
												isInvalid={!!employmentErrors.jobDescription}
											>
												<FormLabel htmlFor="jobDescription">
													Job Description
												</FormLabel>
												<Textarea
													id="jobDescription"
													placeholder="Example: Responsible for handling all electrical installations and maintenance."
													{...registerEmployment("jobDescription")}
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{employmentErrors.jobDescription?.message}
												</FormErrorMessage>
											</FormControl>

											<FormControl isInvalid={!!employmentErrors.startDate}>
												<FormLabel htmlFor="startDate">Start Date</FormLabel>
												<Input
													id="startDate"
													{...registerEmployment("startDate")}
													type="date"
													variant="outline"
													rounded={0}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{employmentErrors.startDate?.message}
												</FormErrorMessage>
											</FormControl>

											<FormControl className="mt-2">
												<div className="flex items-center gap-2">
													<Checkbox
														id="currentlyWorking"
														colorScheme="brand"
														{...registerEmployment("currentlyWorking")}
													/>
													<FormLabel
														fontSize={"14px"}
														margin={0}
														fontWeight={"normal"}
														htmlFor="currentlyWorking"
													>
														I currently work here
													</FormLabel>
												</div>
											</FormControl>

											{!currentlyWorking && (
												<FormControl isInvalid={!!employmentErrors.endDate}>
													<FormLabel htmlFor="endDate">End Date</FormLabel>
													<Input
														id="endDate"
														{...registerEmployment("endDate")}
														type="date"
														variant="outline"
														rounded={0}
														border={"1px solid #D7D7D7"}
													/>
													<FormErrorMessage>
														{employmentErrors.endDate?.message}
													</FormErrorMessage>
												</FormControl>
											)}

											<FormControl isInvalid={!!employmentErrors.monthlySalary}>
												<FormLabel htmlFor="monthlySalary">
													Monthly Salary
												</FormLabel>
												<div className="flex flex-col gap-1">
													<FormControl
														isInvalid={
															!!employmentErrors.proDoesNotWishToDiscloseMonthlySalary
														}
													>
														<div className="flex items-center gap-2">
															<Checkbox
																id="proDoesNotWishToDiscloseMonthlySalary"
																colorScheme="brand"
																{...registerEmployment(
																	"proDoesNotWishToDiscloseMonthlySalary"
																)}
																isDisabled={
																	!!(monthlySalary && monthlySalary > 0)
																}
															/>
															<FormLabel
																fontSize={"14px"}
																color={"gray.500"}
																margin={0}
																fontWeight={"normal"}
																htmlFor="proDoesNotWishToDiscloseMonthlySalary"
															>
																I do not wish to disclose the monthly salary
															</FormLabel>
														</div>
													</FormControl>
													<InputGroup>
														<InputLeftAddon
															rounded={0}
															border={"1px solid #D7D7D7"}
														>
															₦
														</InputLeftAddon>
														<NumberInput
															className="w-full"
															onChange={(_, value) => {
																if (isNaN(value)) {
																	setEmploymentValue("monthlySalary", 0);
																	return;
																}
																setEmploymentValue("monthlySalary", value);
															}}
															isDisabled={
																!!proDoesNotWishToDiscloseMonthlySalary
															}
															value={watchEmployment("monthlySalary")}
															step={10000}
															rounded={0}
															border={"#D7D7D7"}
														>
															<NumberInputField rounded={0} />
															<NumberInputStepper>
																<NumberIncrementStepper />
																<NumberDecrementStepper />
															</NumberInputStepper>
														</NumberInput>
													</InputGroup>
												</div>
												<FormHelperText fontSize={"14px"} color={"gray.500"}>
													This is not displayed publicly
												</FormHelperText>
												<FormErrorMessage>
													{employmentErrors.monthlySalary?.message}
												</FormErrorMessage>
											</FormControl>
										</>
									)}
								</div>

								<div className="flex flex-col items-start gap-2">
									<div className="flex flex-col gap-1">
										<Heading as="h4" className="!text-xl !font-medium">
											{imageSectionTitle}
										</Heading>
										<p className="text-sm text-gray-600">
											{imageSectionDescription}
										</p>
									</div>
									{selectedPortfolioImages.length > 0 && (
										<div className="flex flex-wrap gap-4 w-full">
											{selectedPortfolioImages.map((image, index) => (
												<div key={index} className="relative">
													<img
														src={image}
														alt={`Upload Preview ${index + 1}`}
														className="max-h-[250px] max-w-[300px]"
													/>
													<Button
														position="absolute"
														top={2}
														right={2}
														size="sm"
														colorScheme="red"
														onClick={() => {
															setSelectedPortfolioImages((prev) =>
																prev.filter((_, i) => i !== index)
															);
															if (jobType === "project") {
																const currentImages =
																	watchProject("images") || [];
																setProjectValue(
																	"images",
																	currentImages.filter((_, i) => i !== index)
																);
															} else {
																const currentImages =
																	watchEmployment("images") || [];
																setEmploymentValue(
																	"images",
																	currentImages.filter((_, i) => i !== index)
																);
															}
														}}
													>
														Remove
													</Button>
												</div>
											))}
										</div>
									)}
									<Box position="relative" display="flex" gap={4}>
										<Input
											type="file"
											accept="image/*"
											onChange={handleFileChange}
											id="upload-btn"
											hidden
											multiple
										/>
										<Button
											onClick={() => {
												document.getElementById("upload-btn")?.click();
											}}
											leftIcon={<FaUpload />}
											colorScheme="prussianBlue"
											variant="outline"
										>
											{selectedPortfolioImages.length
												? "Add More Photos"
												: "Choose Photos"}
										</Button>
									</Box>
								</div>
							</div>
						</div>
					</Stack>
					<Stack
						py={{
							base: 2,
							lg: 4,
						}}
						px={{
							base: 8,
							lg: 20,
						}}
						borderTop={"1px"}
						borderColor={"#E7E7E7"}
						alignItems={"end"}
					>
						<Button
							type="submit"
							colorScheme="brand"
							rounded={0}
							px={8}
							py={6}
							rightIcon={<FiArrowRight size={"18px"} />}
							isLoading={
								jobType === "project"
									? addProjectHistoryHandle.loading
									: addEmploymentHistoryItemHandle.loading
							}
						>
							Submit
						</Button>
					</Stack>
				</form>
			</Stack>
		</>
	);
};
